<template>
  <RegistrationLoginComponent
    :actorType="registrationActorType"
    @loginSuccess="loginSuccess"
    @setActorType="setActorType"
    @beforeLoginSuccess="beforeLoginSuccess"
  />
  <ModalFormComponent
    ref="modalForm"
    :models="models"
    :title="$t('registration.group_action.create')"
    :modalConfig="modalConfig"
    :hideCancel="true"
    :showCloseButton="false"
    @submit="submit"
  >
    <template #formView>
      <div v-if="!hasAlreadyActorType" class="mt-3 mb-4 profile">
        <div class="text-center textStyle fw-bold fs-6 mb-2">
          {{ $t("registration.iAm") }}
        </div>
        <div class="d-flex align-items-center justify-content-center">
          <button
            v-for="button in buttons"
            :key="button.condition"
            type="button"
            class="btn mx-2"
            :class="{
              actived: registrationActorType && registrationActorType === button.condition,
            }"
            @click="toggleActorType(button.condition)"
          >
            <FontAwesomeIcon :icon="'fa-light fa-' + button.icon" class="me-2" />
            {{ button.label }}
          </button>
        </div>
      </div>
      <template v-if="displayFullForm">
        <AlertBlock :text="$t('registration.creation.info')" type="info" />
        <div class="row">
          <FormFieldComponent v-for="model in models" :key="model.vars.id" :model="model" />
        </div>
      </template>
      <BlockLoader :loading="loading" />
    </template>
  </ModalFormComponent>
</template>

<script>
import RegistrationLoginComponent from "@/components/registration/RegistrationLoginComponent.vue";
import ModalFormComponent from "@/components/form/ModalFormComponent.vue";
import FormFieldComponent from "@/components/form/FormFieldComponent.vue";
import BlockLoader from "@/components/blocks/BlockLoader.vue";
import AlertBlock from "@/components/blocks/AlertBlock.vue";
import { registrationConstante } from "@/constantes/registration";
import { i18n } from "@/i18n";
import { mapActions, mapState } from "pinia";
import { useMainStore } from "@/store/main/main";
import { useUserStore } from "@/store/user/user";
import { registrationManager } from "@/services/registration/registration-manager";
import { notificationManager } from "@/services/utilities/notification-manager";
import { formManager } from "@/services/form/form-manager";

export default {
  name: "PreRegistrationSheetCreateView",
  components: {
    AlertBlock,
    BlockLoader,
    FormFieldComponent,
    ModalFormComponent,
    RegistrationLoginComponent,
  },
  data() {
    return {
      models: {},
      loading: false,
      registrationActorType: null,
      hasAlreadyActorType: false,
      displayFullForm: false,
      isHandlingLogin: false,
      modalConfig: { backdrop: "static", keyboard: false },
      buttons: [
        {
          label: this.$t("registration.anApprentice"),
          condition: registrationConstante.REGISTRATION_ACTOR_APPRENTICE,
          icon: "user",
        },
        {
          label: this.$t("registration.aCompany"),
          condition: registrationConstante.REGISTRATION_ACTOR_COMPANY,
          icon: "building",
        },
      ],
    };
  },
  computed: {
    registrationConstante() {
      return registrationConstante;
    },

    ...mapState(useUserStore, {
      user: (store) => store.user,
    }),
  },
  watch: {
    user() {
      if (this.isHandlingLogin) return;
      this.init();
    },
  },
  mounted() {
    this.init(true);
  },
  methods: {
    prepareFormPayload(models) {
      const result = formManager.processForm(models);
      delete result.password;
      delete result.repeatedPassword;
      return {
        data: result,
        registrationActorType: this.registrationActorType,
      };
    },

    sendCreateForm(data = {}) {
      return registrationManager.postRegistrationCreateForm(this.$route.params.group, data);
    },

    async fetchCreationForm() {
      this.loading = true;
      const params = this.registrationActorType ? { registrationActorType: this.registrationActorType } : {};

      this.models = await registrationManager.getRegistrationCreateForm(params);
      this.loading = false;
    },

    init(mounted = false) {
      this.registrationActorType = this.user?.targetParams?.registrationActorType;

      if (this.registrationActorType === registrationConstante.REGISTRATION_ACTOR_APPRENTICE && this.user.birthDate) {
        this.sendCreateForm({ data: {}, registrationActorType: this.registrationActorType });
      } else if (!mounted) {
        this.setActorType(this.registrationActorType, true);
        this.fetchCreationForm();
      }
      this.setAppLoading(false);
    },

    async loginSuccess(payload) {
      this.loading = true;
      this.registrationActorType = payload.type ? payload.type : this.user.targetParams.registrationActorType;
      const birthDate = payload?.models?.birthday?.vars?.value
        ? payload?.models?.birthday?.vars?.value
        : this.user.birthDate;

      if (
        !this.registrationActorType ||
        this.registrationActorType === registrationConstante.REGISTRATION_ACTOR_COMPANY ||
        (this.registrationActorType === registrationConstante.REGISTRATION_ACTOR_APPRENTICE && !birthDate)
      ) {
        this.hasAlreadyActorType = true;
        this.displayFullForm = true;
        await this.fetchCreationForm();
        if (this.registrationActorType === registrationConstante.REGISTRATION_ACTOR_APPRENTICE) {
          this.models.firstName.vars.value = this.user.firstName;
          this.models.lastName.vars.value = this.user.lastName;
          this.models.email.vars.value = this.user.email;
        }
        return;
      }

      this.setAppLoading(true);
      const data = this.prepareFormPayload(payload.models);
      this.sendCreateForm(data);
    },

    beforeLoginSuccess() {
      this.isHandlingLogin = true;
    },

    setActorType(actorType, init = false) {
      this.registrationActorType = actorType;
      if (this.registrationActorType && init) {
        this.hasAlreadyActorType = true;
      }
    },

    submit() {
      this.setAppLoading(true);
      this.loading = true;
      delete this.models.submit;

      const data = this.prepareFormPayload(this.models);
      this.sendCreateForm(data);
    },

    toggleActorType(actorType) {
      this.setActorType(actorType);

      if (actorType === registrationConstante.REGISTRATION_ACTOR_APPRENTICE && this.user.birthDate) {
        this.handleApprenticeResponse();
      } else {
        this.displayFullForm = true;
        this.preSetApprenticeData();
      }
    },

    preSetApprenticeData() {
      if (this.registrationActorType === registrationConstante.REGISTRATION_ACTOR_APPRENTICE) {
        this.models.lastName.vars.value = this.user.lastName;
        this.models.firstName.vars.value = this.user.firstName;
        this.models.email.vars.value = this.user.email;
      } else {
        this.models.lastName.vars.value = null;
        this.models.firstName.vars.value = null;
        this.models.email.vars.value = null;
      }
    },

    async handleApprenticeResponse() {
      const alert = await notificationManager.showAlert(
        "question",
        i18n.global.t("registration.create.confirmation"),
        "",
        true,
        true,
      );

      if (!alert.isConfirmed) return;

      this.$refs.modalForm.cancel();
      this.sendCreateForm({ data: {}, registrationActorType: this.registrationActorType });
    },

    ...mapActions(useMainStore, ["setAppLoading"]),
  },
};
</script>

<style lang="scss" scoped>
.btn {
  background: $white !important;
  border-color: $blue !important;
  color: $blue !important;
}

.actived {
  background: $blue !important;
  color: $white !important;

  &:hover,
  &:focus {
    background: $blue !important;
    color: $white !important;
  }
}
</style>
